import { MessageContextItemKind } from '@affine/graphql';
import { z } from 'zod';

export type {
  CopilotClientActionPending,
  WorkspaceNavigationAck,
} from '@streamlain/copilot-realtime-contract';

import { AgentAffectedRefSchema } from '../../../../modules/streamlain/agent-activity-types';

export const StreamObjectSchema = z.discriminatedUnion('type', [
  z.object({
    type: z.literal('text-delta'),
    textDelta: z.string(),
  }),
  z.object({
    type: z.literal('reasoning'),
    textDelta: z.string(),
    /** How long the model thought; stored with the answer, absent while it streams. */
    durationMs: z.number().nullish(),
  }),
  z.object({
    type: z.literal('tool-call'),
    toolCallId: z.string(),
    toolName: z.string(),
    displayName: z.string().optional(),
    args: z.record(z.string(), z.any()),
  }),
  z.object({
    type: z.literal('tool-result'),
    toolCallId: z.string(),
    toolName: z.string(),
    displayName: z.string().optional(),
    args: z.record(z.string(), z.any()),
    result: z.any().optional(),
    affectedRefs: z.array(AgentAffectedRefSchema).optional(),
    /** The tool only read: its refs name where it looked, not what it changed. */
    readOnly: z.boolean().optional(),
    /** How long the step ran, from its call to its result. */
    durationMs: z.number().nullish(),
  }),
  z.object({
    type: z.literal('tool-execution-started'),
    toolCalls: z.array(
      z.object({
        name: z.string(),
        displayName: z.string().optional(),
        count: z.number(),
      })
    ),
    totalCount: z.number(),
  }),
  // sl-opil.15.3: Usage/credits info for managed AI calls
  z.object({
    type: z.literal('usage'),
    creditsUsed: z.number(),
    model: z.string(),
    promptTokens: z.number().optional(),
    completionTokens: z.number().optional(),
    totalTokens: z.number().optional(),
  }),
  // sl-k8wk.2.2: Low-balance warning metadata from trackStreamUsage()
  z.object({
    type: z.literal('meta'),
    lowBalance: z.boolean().optional(),
    remainingPercent: z.number().optional(),
  }),
  // sl-ufq6.7.1: Budget warning (warn mode — AI call continued)
  z.object({
    type: z.literal('warning'),
    code: z.string(),
    message: z.string(),
    resetAt: z.string().optional(),
    spentMicro: z.string().optional(),
    limitMicro: z.string().nullable().optional(),
  }),
  // sl-sf9o: Model routing telemetry
  z.object({
    type: z.literal('routing'),
    tier: z.string(),
    complexity: z.string(),
    score: z.number(),
    resolvedModel: z.string(),
  }),
]);

export type StreamObject = z.infer<typeof StreamObjectSchema>;

/**
 * sl-canvas2-8.31 Phase 6: Message types for Two-Call Pattern
 */
export const MessageTypeEnum = z.enum([
  'text',
  'tool_call',
  'tool_approval_request',
  'tool_approval_response',
  'tool_result',
  'goal_turn',
]);

export type MessageType = z.infer<typeof MessageTypeEnum>;

/**
 * sl-canvas2-8.31 Phase 6: Tool call info
 */
const ToolCallInfoSchema = z.object({
  name: z.string(),
  arguments: z.record(z.string(), z.any()).optional(),
});

/**
 * GraphQL returns every unset tool data field as null, while the fields are
 * optional here; dropping the null fields keeps saved chat blocks readable.
 */
function withoutNullFields(value: unknown): unknown {
  if (!value || typeof value !== 'object' || Array.isArray(value)) return value;
  return Object.fromEntries(
    Object.entries(value).filter(([, field]) => field !== null)
  );
}

/**
 * sl-canvas2-8.31 Phase 6: Tool-related data for typed messages
 */
const ToolMessageDataSchema = z.preprocess(
  withoutNullFields,
  z.object({
    // For a goal turn's message: the goal and what the turn does
    goalTurn: z
      .object({
        goalId: z.string(),
        kind: z.enum(['plan', 'step', 'verify']),
        stepLabel: z.string().nullish(),
      })
      .optional(),
    // For tool-approval-request
    approvalId: z.string().optional(),
    toolCall: ToolCallInfoSchema.optional(),
    description: z.string().optional(),
    confirmationCopy: z
      .object({
        key: z.string(),
        params: z
          .record(
            z.string(),
            z.union([z.string(), z.number(), z.boolean(), z.null()])
          )
          .optional(),
      })
      .optional(),
    // For tool-approval-response
    approved: z.boolean().optional(),
    reason: z.string().optional(),
    includesAgentMode: z.boolean().optional(),
    includesTargetMode: z.boolean().optional(),
    // For tool-result
    toolCallId: z.string().optional(),
    toolName: z.string().optional(),
    success: z.boolean().optional(),
    result: z.any().optional(),
    error: z.string().optional(),
    // sl-jkhk: Embedded tool results in text messages for history rendering
    toolResults: z
      .array(
        z.object({
          toolCallId: z.string(),
          toolName: z.string(),
          displayName: z.string().optional(),
          success: z.boolean(),
          // sl-vt9e: Optional result data for rich reconstruction (KG sources, counts)
          result: z.any().optional(),
          affectedRefs: z.array(AgentAffectedRefSchema).optional(),
          durationMs: z.number().nullish(),
        })
      )
      .optional(),
    // sl-tkri.3.1: Persisted credit charge for history display (survives reload)
    creditsUsed: z.number().optional(),
    // sl-tkri.3.1: Model used (for credit badge tooltip)
    model: z.string().optional(),
  })
);

export type ToolMessageData = z.infer<typeof ToolMessageDataSchema>;

export type JsonValue =
  | string
  | number
  | boolean
  | null
  | JsonValue[]
  | { [key: string]: JsonValue };

const JsonValueSchema: z.ZodType<JsonValue> = z.lazy(() =>
  z.union([
    z.string(),
    z.number(),
    z.boolean(),
    z.null(),
    z.array(JsonValueSchema),
    z.record(z.string(), JsonValueSchema),
  ])
);

const MessageContextItemKindSchema = z.enum(
  Object.values(MessageContextItemKind) as [
    `${MessageContextItemKind}`,
    ...`${MessageContextItemKind}`[],
  ]
);

const ContextSourceSchema = z.enum([
  'CLIENT_ACTION',
  'FOCUS',
  'FULLSCREEN',
  'LAST_ACTIVE',
  'PASTE',
]);

const ContextPrioritySchema = z.enum(['HIGHEST', 'MID', 'LOWEST']);

const MessageContextDisplaySchema = z.object({
  title: z.string(),
  subtitle: z.string().nullish(),
  icon: z.string().nullish(),
  preview: z.string().nullish(),
  unavailable: z.boolean().nullish(),
  unavailableReason: z.string().nullish(),
});

const MessageContextItemSchema = z.object({
  id: z.string(),
  kind: MessageContextItemKindSchema,
  label: z.string(),
  subtitle: z.string().nullish(),
  source: ContextSourceSchema,
  priority: ContextPrioritySchema,
  display: MessageContextDisplaySchema,
  refs: z.record(z.string(), JsonValueSchema),
  payload: z.record(z.string(), JsonValueSchema).nullish(),
});

const MessageContextSchema = z
  .object({
    docId: z.string().nullish(),
    searchMode: z.string().nullish(),
    items: z.array(MessageContextItemSchema),
  })
  .nullish();

const AssistantSourceItemKindSchema = z.enum([
  'doc-search',
  'mail',
  'web',
  'created-doc',
]);

const AssistantSourceItemSchema = z.object({
  id: z.string(),
  kind: AssistantSourceItemKindSchema,
  label: z.string(),
  display: MessageContextDisplaySchema,
  refs: z.record(z.string(), JsonValueSchema),
  payload: z.record(z.string(), JsonValueSchema).nullish(),
});

const AssistantSourceManifestSchema = z
  .object({
    items: z.array(AssistantSourceItemSchema),
  })
  .nullish();

const ChatMessageSchema = z.object({
  id: z.string(),
  content: z.string(),
  role: z.union([z.literal('user'), z.literal('assistant')]),
  createdAt: z.string(),
  // Use .nullish() to accept both null and undefined from backend
  streamObjects: z.array(StreamObjectSchema).nullish(),
  attachments: z.array(z.string()).nullish(),
  context: MessageContextSchema,
  sources: AssistantSourceManifestSchema,
  userId: z.string().nullish(),
  userName: z.string().nullish(),
  avatarUrl: z.string().nullish(),
  // sl-canvas2-8.31 Phase 6: Two-Call Pattern fields
  messageType: MessageTypeEnum.nullish(),
  toolData: ToolMessageDataSchema.nullish(),
});

export const ChatMessagesSchema = z.array(ChatMessageSchema);

export type ChatMessage = z.infer<typeof ChatMessageSchema>;

export type ChatAction = {
  action: string;
  messages: ChatMessage[];
  sessionId: string;
  createdAt: string;
};

export type HistoryMessage = ChatMessage | ChatAction;

export type MessageRole = 'user' | 'assistant';

export type MessageUserInfo = {
  userId?: string;
  userName?: string;
  avatarUrl?: string;
};

export function isChatAction(item: HistoryMessage): item is ChatAction {
  return 'action' in item;
}

export function isChatMessage(item: HistoryMessage): item is ChatMessage {
  return 'role' in item;
}

export type ChatStatus =
  | 'loading'
  | 'success'
  | 'error'
  | 'idle'
  | 'transmitting';

/**
 * sl-canvas2-8.31: Inline confirmation message type
 * Used for destructive AI operations that require user confirmation
 */
export interface ConfirmationMessage {
  type: 'confirmation';
  id: string;
  toolName: string;
  displayName: string;
  description: string;
  copy?: {
    key: string;
    params?: Record<string, string | number | boolean | null>;
  };
  title?: string;
  confirmLabel?: string;
  cancelLabel?: string;
  settingsHint?: string | null;
  metadata?: Record<string, unknown>;
  /** Set when user has responded */
  responded?: boolean;
  /** Set to true if approved, false if rejected */
  approved?: boolean;
  /** Tool result after execution */
  toolResult?: {
    success: boolean;
    data?: unknown;
    error?: string;
    errorCode?: string;
    hint?: string;
  };
}

// ==========================================================================
// sl-55fc: Canonical Tool Card Data
// Single format for all tool card states (live, history, confirmation).
// ==========================================================================

export type ToolCardStatus =
  | 'pending'
  | 'running'
  | 'completed'
  | 'failed'
  | 'awaiting-approval';

const ToolCardDataSchema = z.object({
  toolCallId: z.string(),
  toolName: z.string(),
  displayName: z.string(),
  status: z.enum([
    'pending',
    'running',
    'completed',
    'failed',
    'awaiting-approval',
  ]),
  args: z.record(z.string(), z.unknown()).optional(),
  result: z
    .object({
      success: z.boolean(),
      error: z.string().optional(),
      data: z.unknown().optional(),
    })
    .optional(),
});

/**
 * sl-55fc.1: Canonical data format for tool cards.
 * Used by the single renderStreamObjects() path for both live and history.
 */
export type ToolCardData = z.infer<typeof ToolCardDataSchema>;

export { ToolCardDataSchema };

/**
 * sl-55fc.1: Convert a tool-call or tool-result StreamObject to ToolCardData.
 */
export function streamObjectToToolCard(so: StreamObject): ToolCardData | null {
  if (so.type === 'tool-call') {
    return {
      toolCallId: so.toolCallId,
      toolName: so.toolName,
      displayName: so.displayName || so.toolName,
      status: 'running',
      args: so.args,
    };
  }
  if (so.type === 'tool-result') {
    const success = so.result?.success !== false;
    return {
      toolCallId: so.toolCallId,
      toolName: so.toolName,
      displayName: so.displayName || so.toolName,
      status: success ? 'completed' : 'failed',
      args: so.args,
      result: {
        success,
        error: so.result?.error,
        data: so.result?.data,
      },
    };
  }
  return null;
}

/**
 * sl-55fc.1: Convert a persisted ToolResultSummary to ToolCardData.
 */
export function toolResultToToolCard(tr: {
  toolCallId: string;
  toolName: string;
  displayName?: string;
  success: boolean;
}): ToolCardData {
  return {
    toolCallId: tr.toolCallId,
    toolName: tr.toolName,
    displayName: tr.displayName || tr.toolName,
    status: tr.success ? 'completed' : 'failed',
    result: { success: tr.success },
  };
}

/**
 * sl-55fc.1: Convert a persisted ToolResultSummary to a tool-result StreamObject.
 * Used by reconstructStreamObjects() to unify the history rendering path.
 */
export function toolResultToStreamObject(tr: {
  toolCallId: string;
  toolName: string;
  displayName?: string;
  success: boolean;
  result?: unknown;
  affectedRefs?: z.infer<typeof AgentAffectedRefSchema>[];
  durationMs?: number | null;
}): StreamObject {
  return {
    type: 'tool-result' as const,
    toolCallId: tr.toolCallId,
    toolName: tr.toolName,
    ...(tr.displayName ? { displayName: tr.displayName } : {}),
    args: {},
    // sl-vt9e: Use persisted result data if available (KG sources, counts),
    // fall back to simple success/fail for basic tool cards
    result: tr.result ?? { success: tr.success },
    ...(tr.affectedRefs?.length ? { affectedRefs: tr.affectedRefs } : {}),
    ...(typeof tr.durationMs === 'number' ? { durationMs: tr.durationMs } : {}),
  };
}

/**
 * sl-55fc.1: Convert a ConfirmationMessage to ToolCardData.
 */
export function confirmationToToolCard(cm: ConfirmationMessage): ToolCardData {
  return {
    toolCallId: cm.id,
    toolName: cm.toolName,
    displayName: cm.displayName,
    status: cm.responded
      ? cm.approved
        ? cm.toolResult?.success
          ? 'completed'
          : 'failed'
        : 'failed'
      : 'awaiting-approval',
    result: cm.toolResult
      ? {
          success: cm.toolResult.success,
          error: cm.toolResult.error,
        }
      : undefined,
  };
}
